import { useState, useCallback, useRef } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface TextRegion {
  id: string;
  text: string;
  confidence: number;
  category: TextCategory;
  coordinates?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  language?: string;
  metadata?: Record<string, any>;
}

export type TextCategory = 
  | 'amount'
  | 'date'
  | 'supplier_name'
  | 'supplier_address'
  | 'account_number'
  | 'reference_number'
  | 'customer_name'
  | 'customer_address'
  | 'service_type'
  | 'meter_reading'
  | 'period'
  | 'header'
  | 'footer'
  | 'unknown';

export interface FieldSuggestion {
  fieldName: string;
  confidence: number;
  reasoning: string;
  category: TextCategory;
}

export interface TextAnalysisResult {
  category: TextCategory;
  confidence: number;
  language: string;
  suggestions: FieldSuggestion[];
  metadata: {
    isNumeric: boolean;
    isDate: boolean;
    isCurrency: boolean;
    hasPhoneNumber: boolean;
    hasEmail: boolean;
    hasAddress: boolean;
    wordCount: number;
    characterCount: number;
  };
}

const FIELD_MAPPING: Record<TextCategory, string[]> = {
  amount: ['amount', 'total', 'totalAmount', 'dueAmount', 'balance'],
  date: ['date', 'dueDate', 'issueDate', 'periodStart', 'periodEnd'],
  supplier_name: ['supplierName', 'companyName', 'vendorName'],
  supplier_address: ['supplierAddress', 'billingAddress'],
  account_number: ['accountNumber', 'customerAccount', 'serviceAccount'],
  reference_number: ['referenceNumber', 'billNumber', 'invoiceNumber'],
  customer_name: ['customerName', 'accountHolder'],
  customer_address: ['customerAddress', 'serviceAddress'],
  service_type: ['serviceType', 'utilityType'],
  meter_reading: ['meterReading', 'currentReading', 'previousReading'],
  period: ['billingPeriod', 'servicePeriod'],
  header: [],
  footer: [],
  unknown: []
};

export const useAITextRecognition = () => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisHistory, setAnalysisHistory] = useState<TextAnalysisResult[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { toast } = useToast();

  // Detect language of the text
  const detectLanguage = useCallback((text: string): string => {
    const patterns = {
      en: /\b(electricity|gas|water|bill|amount|due|total|account|meter|reading)\b/i,
      es: /\b(electricidad|gas|agua|factura|cantidad|vencimiento|total|cuenta|medidor|lectura)\b/i,
      fr: /\b(électricité|gaz|eau|facture|montant|échéance|total|compte|compteur|lecture)\b/i,
      de: /\b(strom|gas|wasser|rechnung|betrag|fällig|gesamt|konto|zähler|ablesung)\b/i,
      pt: /\b(eletricidade|gás|água|fatura|quantidade|vencimento|total|conta|medidor|leitura)\b/i
    };

    for (const [lang, pattern] of Object.entries(patterns)) {
      if (pattern.test(text)) {
        return lang;
      }
    }
    
    // Simple character-based detection for some languages
    if (/[\u00C0-\u017F]/.test(text)) return 'es'; // Spanish/French accents
    if (/[\u00DF\u00C4\u00D6\u00DC\u00E4\u00F6\u00FC]/.test(text)) return 'de'; // German
    if (/[\u00C7\u00E7\u00C3\u00E3\u00D5\u00F5]/.test(text)) return 'pt'; // Portuguese
    
    return 'en'; // Default to English
  }, []);

  // Generate metadata about the text
  const generateMetadata = useCallback((text: string) => {
    const isNumeric = /^\d+([.,]\d+)*$/.test(text.trim());
    const isCurrency = /[\$€£¥₹]\s*\d+|^\d+([.,]\d+)*\s*[\$€£¥₹]?$/.test(text);
    const isDate = /\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}|\d{4}[\/\-\.]\d{1,2}[\/\-\.]\d{1,2}/.test(text);
    const hasPhoneNumber = /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/.test(text);
    const hasEmail = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(text);
    const hasAddress = /\d+\s+[A-Za-z\s,]+(?:street|st|avenue|ave|road|rd|lane|ln|drive|dr|blvd|boulevard)/i.test(text);
    
    return {
      isNumeric,
      isDate,
      isCurrency,
      hasPhoneNumber,
      hasEmail,
      hasAddress,
      wordCount: text.trim().split(/\s+/).length,
      characterCount: text.length
    };
  }, []);

  // Categorize text based on patterns and context
  const categorizeText = useCallback((text: string, metadata: any): { category: TextCategory; confidence: number } => {
    const lowerText = text.toLowerCase().trim();
    
    // Currency/Amount detection
    if (metadata.isCurrency || /total|amount|due|balance|charge|cost|price/i.test(text)) {
      return { category: 'amount', confidence: 0.9 };
    }
    
    // Date detection
    if (metadata.isDate || /date|due|period|from|to|until/i.test(text)) {
      return { category: 'date', confidence: 0.85 };
    }
    
    // Account/Reference numbers
    if (metadata.isNumeric && text.length > 6 && /account|acct|ref|reference|bill|invoice/i.test(text)) {
      return { category: 'account_number', confidence: 0.8 };
    }
    
    // Supplier detection
    if (/company|corp|inc|ltd|llc|electric|gas|water|utility/i.test(text) && metadata.wordCount <= 5) {
      return { category: 'supplier_name', confidence: 0.75 };
    }
    
    // Address detection
    if (metadata.hasAddress || (/\d+.*(?:street|st|avenue|ave|road|rd)/i.test(text) && metadata.wordCount > 2)) {
      if (/po box|billing|mail/i.test(text)) {
        return { category: 'supplier_address', confidence: 0.7 };
      }
      return { category: 'customer_address', confidence: 0.7 };
    }
    
    // Name detection
    if (metadata.wordCount >= 2 && metadata.wordCount <= 4 && /^[A-Z][a-z]+(\s[A-Z][a-z]+)+$/.test(text)) {
      return { category: 'customer_name', confidence: 0.6 };
    }
    
    // Service type
    if (/electric|electricity|gas|water|sewer|waste|internet|phone|cable/i.test(text)) {
      return { category: 'service_type', confidence: 0.8 };
    }
    
    // Meter readings
    if (metadata.isNumeric && /meter|reading|kwh|mcf|gallons|usage/i.test(text)) {
      return { category: 'meter_reading', confidence: 0.75 };
    }
    
    // Header/Footer detection
    if (metadata.wordCount > 10 || /header|footer|terms|conditions|notice/i.test(text)) {
      return { category: 'header', confidence: 0.3 };
    }
    
    return { category: 'unknown', confidence: 0.1 };
  }, []);

  // Generate field suggestions based on category
  const generateFieldSuggestions = useCallback((
    category: TextCategory,
    confidence: number,
    text: string,
    metadata: any
  ): FieldSuggestion[] => {
    const possibleFields = FIELD_MAPPING[category] || [];
    
    return possibleFields.map(fieldName => {
      let fieldConfidence = confidence;
      let reasoning = `Categorized as ${category}`;
      
      // Boost confidence for specific patterns
      if (category === 'amount' && metadata.isCurrency) {
        fieldConfidence = Math.min(0.95, fieldConfidence + 0.1);
        reasoning += ' with currency formatting';
      }
      
      if (category === 'date' && metadata.isDate) {
        fieldConfidence = Math.min(0.95, fieldConfidence + 0.1);
        reasoning += ' with date formatting';
      }
      
      // Field-specific adjustments
      if (fieldName === 'totalAmount' && /total|final|due/i.test(text)) {
        fieldConfidence = Math.min(0.95, fieldConfidence + 0.15);
        reasoning += ' (likely total amount)';
      }
      
      if (fieldName === 'dueDate' && /due|payment|pay/i.test(text)) {
        fieldConfidence = Math.min(0.95, fieldConfidence + 0.15);
        reasoning += ' (likely due date)';
      }
      
      return {
        fieldName,
        confidence: fieldConfidence,
        reasoning,
        category
      };
    }).sort((a, b) => b.confidence - a.confidence);
  }, []);

  // Main analysis function - now uses AI edge function
  const analyzeText = useCallback(async (
    text: string,
    options: {
      includeRegions?: boolean;
      includeMetadata?: boolean;
      previousContext?: string[];
    } = {}
  ): Promise<TextAnalysisResult> => {
    if (!text.trim()) {
      throw new Error('No text provided for analysis');
    }

    setIsAnalyzing(true);
    
    try {
      // Abort previous analysis if running
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      abortControllerRef.current = new AbortController();
      
      // Try AI-powered analysis first
      try {
        const { data, error } = await supabase.functions.invoke('ai-text-recognition', {
          body: {
            text: text.trim(),
            operation: 'analyze',
            options: {
              includeMetadata: options.includeMetadata !== false,
              includeFieldSuggestions: true,
              documentType: 'utility bill',
              context: options.previousContext?.join(' ')
            }
          }
        });

        if (error) {
          console.warn('AI analysis failed, using fallback:', error);
          throw new Error('AI analysis failed');
        }

        // Map AI response to our expected format
        const result: TextAnalysisResult = {
          category: mapAICategoryToLocal(data.category),
          confidence: data.confidence || 0.5,
          language: data.language || 'en',
          suggestions: data.fieldSuggestions?.map((suggestion: any) => ({
            fieldName: suggestion.fieldName,
            confidence: suggestion.confidence,
            reasoning: suggestion.reasoning,
            category: mapAICategoryToLocal(data.category)
          })) || [],
          metadata: {
            isNumeric: data.metadata?.hasNumbers || false,
            isDate: data.metadata?.hasDate || false,
            isCurrency: data.metadata?.hasCurrency || false,
            hasPhoneNumber: data.metadata?.hasPhone || false,
            hasEmail: data.metadata?.hasEmail || false,
            hasAddress: /address|street|avenue|road|drive|lane/i.test(text),
            wordCount: data.metadata?.wordCount || text.split(/\s+/).length,
            characterCount: data.metadata?.charCount || text.length
          }
        };

        // Store in history
        setAnalysisHistory(prev => [result, ...prev.slice(0, 9)]);
        
        return result;

      } catch (aiError) {
        console.warn('AI analysis failed, using local fallback:', aiError);
        
        // Fallback to local analysis
        const language = detectLanguage(text);
        const metadata = generateMetadata(text);
        const { category, confidence } = categorizeText(text, metadata);
        const suggestions = generateFieldSuggestions(category, confidence, text, metadata);
        
        const result: TextAnalysisResult = {
          category,
          confidence: confidence * 0.8, // Reduce confidence for fallback
          language,
          suggestions,
          metadata
        };
        
        // Store in history
        setAnalysisHistory(prev => [result, ...prev.slice(0, 9)]);
        
        return result;
      }
      
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('Analysis was cancelled');
      }
      throw error;
    } finally {
      setIsAnalyzing(false);
    }
  }, [detectLanguage, generateMetadata, categorizeText, generateFieldSuggestions]);

  // Map AI categories to local categories
  const mapAICategoryToLocal = useCallback((aiCategory: string): TextCategory => {
    const mapping: Record<string, TextCategory> = {
      'customer_name': 'customer_name',
      'account_number': 'account_number',
      'bill_date': 'date',
      'amount': 'amount',
      'due_date': 'date',
      'supplier_name': 'supplier_name',
      'service_type': 'service_type',
      'billing_period_start': 'period',
      'billing_period_end': 'period',
      'usage_amount': 'meter_reading',
      'usage_unit': 'meter_reading',
      'address': 'customer_address',
      'phone': 'unknown',
      'email': 'unknown',
      'other': 'unknown'
    };
    
    return mapping[aiCategory] || 'unknown';
  }, []);

  // Enhanced text region detection for PDF content - now uses AI service
  const detectTextRegions = useCallback(async (
    documentText: string,
    options: {
      minConfidence?: number;
      categories?: TextCategory[];
    } = {}
  ): Promise<TextRegion[]> => {
    const { minConfidence = 0.3, categories } = options;
    
    setIsAnalyzing(true);
    
    try {
      // Try AI-powered region detection first
      const { data, error } = await supabase.functions.invoke('ai-text-recognition', {
        body: {
          text: documentText,
          operation: 'detect_regions',
          options: {
            minConfidence,
            categories: categories?.map(cat => mapLocalCategoryToAI(cat))
          }
        }
      });

      if (!error && Array.isArray(data)) {
        return data.map((region: any, index: number) => ({
          id: `region-${index}`,
          text: region.text,
          confidence: region.confidence,
          category: mapAICategoryToLocal(region.category),
          coordinates: region.coordinates,
          language: region.language || 'en',
          metadata: region.metadata
        })).sort((a, b) => b.confidence - a.confidence);
      }
    } catch (aiError) {
      console.warn('AI region detection failed, using fallback:', aiError);
    }
    
    // Fallback to line-by-line analysis
    const lines = documentText.split('\n').filter(line => line.trim().length > 0);
    const regions: TextRegion[] = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line.length < 2) continue;
      
      try {
        const analysis = await analyzeText(line);
        
        if (analysis.confidence >= minConfidence) {
          if (!categories || categories.includes(analysis.category)) {
            regions.push({
              id: `region-${i}`,
              text: line,
              confidence: analysis.confidence,
              category: analysis.category,
              language: analysis.language,
              metadata: analysis.metadata
            });
          }
        }
      } catch (error) {
        console.warn('Error analyzing line:', line, error);
      }
    }
    
    setIsAnalyzing(false);
    return regions.sort((a, b) => b.confidence - a.confidence);
  }, [analyzeText, mapAICategoryToLocal]);

  // Map local categories to AI categories
  const mapLocalCategoryToAI = useCallback((localCategory: TextCategory): string => {
    const mapping: Record<TextCategory, string> = {
      'customer_name': 'customer_name',
      'account_number': 'account_number',
      'date': 'bill_date',
      'amount': 'amount',
      'supplier_name': 'supplier_name',
      'supplier_address': 'address',
      'customer_address': 'address',
      'service_type': 'service_type',
      'meter_reading': 'usage_amount',
      'period': 'billing_period_start',
      'reference_number': 'account_number',
      'header': 'other',
      'footer': 'other',
      'unknown': 'other'
    };
    
    return mapping[localCategory] || 'other';
  }, []);

  // Get quality score for extracted text
  const getQualityScore = useCallback((text: string): {
    score: number;
    factors: string[];
    recommendations: string[];
  } => {
    const factors: string[] = [];
    const recommendations: string[] = [];
    let score = 1.0;
    
    // Length factor
    if (text.length < 3) {
      score -= 0.4;
      factors.push('Very short text');
      recommendations.push('Select longer text segments for better recognition');
    }
    
    // Character quality
    const specialCharRatio = (text.match(/[^\w\s.,!?-]/g) || []).length / text.length;
    if (specialCharRatio > 0.3) {
      score -= 0.3;
      factors.push('Many special characters detected');
      recommendations.push('Text may contain OCR artifacts');
    }
    
    // Word completeness
    const incompleteWords = text.match(/\b\w{1,2}\b/g) || [];
    if (incompleteWords.length > text.split(' ').length * 0.3) {
      score -= 0.2;
      factors.push('Many short words detected');
      recommendations.push('Text may be fragmented');
    }
    
    // Consistency
    const hasNumbers = /\d/.test(text);
    const hasLetters = /[a-zA-Z]/.test(text);
    if (hasNumbers && hasLetters && text.length > 10) {
      score += 0.1;
      factors.push('Mixed content with good length');
    }
    
    return {
      score: Math.max(0, Math.min(1, score)),
      factors,
      recommendations
    };
  }, []);

  return {
    isAnalyzing,
    analysisHistory,
    analyzeText,
    detectTextRegions,
    getQualityScore,
    detectLanguage,
    generateMetadata,
    categorizeText
  };
};